/**
 * Labstep
 *
 * @module components/Molecule/DataGrid/coldefs/structure
 * @desc Column definition for molecule structure (svg)
 */

import type { ValueGetterParams } from 'ag-grid-community';
import EntityActionCreate from 'labstep-web/components/Entity/Action/Create';
import { MoleculeActionEditContainer as MoleculeActionEdit } from 'labstep-web/components/Molecule/Action/Edit';
import { MoleculeStructureContainer as MoleculeStructure } from 'labstep-web/components/Molecule/Structure';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  getEditable,
  getEntityDefault,
  setValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import { Molecule } from 'labstep-web/models/molecule.model';
import type { colDefMoleculeType } from '../types';

/**
 * Column definition for molecule structure (svg)
 * @param options Options
 * @returns Column definition
 */
const colDefStructure: colDefMoleculeType = (options = {}) => {
  const { getNestedEntity, columnProps, getCreateProps } = options;
  const editable = getEditable(getNestedEntity);
  const getEntity = getNestedEntity || getEntityDefault;

  return {
    colId: 'structure',
    cellEditor: (params: CellEditorParams<Entity>) => {
      const entity = getEntity(params as any);
      if (!(entity instanceof Molecule)) {
        return null;
      }
      return (
        <ReadOnMountHOC
          type="entities"
          entityName={entity.entityName}
          params={{
            get_single: 1,
            guid: entity.guid,
          }}
          loading={{ loader: false }}
          children={({ entity: molecule }) => (
            <MoleculeActionEdit
              molecule={molecule}
              canEdit
              viewComponent={null}
              open
              onDone={(): void => stopEditingAndFocus(params)}
            />
          )}
        />
      );
    },
    cellEditorPopup: true,
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.data) {
        return '';
      }
      const entity = getEntity(params);
      const createProps = getCreateProps?.(params);
      if (createProps) {
        return (
          <EntityActionCreate
            parentName={createProps.parentName}
            parentId={createProps.parentId}
            entityName={Metadata.entityName}
            actionComponentProps={{
              type: 'text',
              text: 'Draw structure',
              elementProps: { placeholder: true, gridCell: true },
            }}
            body={createProps.createBody}
            options={{
              onSuccess: () =>
                params.api.startEditingCell({
                  rowIndex: params.rowIndex,
                  colKey: params.column,
                }),
            }}
          />
        );
      }
      if (!(entity instanceof Molecule)) {
        return null;
      }
      return (
        <MoleculeStructure
          molecule={entity}
          fallback={
            <DataGridPlaceholder
              params={params}
              children="Draw structure"
              editable={editable}
            />
          }
        />
      );
    },
    headerName: 'Structure',
    editable,
    valueGetter: (params: ValueGetterParams): string | null => {
      const entity = getEntity(params);
      if (entity instanceof Molecule) {
        return entity.svg;
      }
      return null;
    },
    valueSetter: (params: any): boolean => {
      const entity = getEntity(params);
      if (!entity) {
        return false;
      }
      return setValue('svg', params.newValue, {
        entityName: entity.entityName,
        id: entity.idAttr,
      });
    },
    ...columnProps,
  };
};

export default colDefStructure;
